import {DataTestAttrs} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import ReactDOM from 'react-dom';
import isEqual from 'react-fast-compare';

import {ArchivableOptionData} from 'search/filters/base/useFilterArchivedDropdownOptions';
import {BLUE_LIGHT, GRAY, GRAY_DARKER, GRAY_LIGHTER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu, {MenuProps} from 'ui-library/menus/Menu';
import MenuOptionWithFloatingSubmenu from 'ui-library/menus/MenuOptionWithFloatingSubmenu';
import {
  defaultRenderOptionContent,
  getMatchingOptionDatas,
  getOptionRefForIndex,
  hasSubmenus,
} from 'ui-library/menus/MenuUtil';
import Option from 'ui-library/menus/Option';
import OptionContainer from 'ui-library/menus/OptionContainer';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import PreventParentClickWrapper from 'ui-library/wrappers/PreventParentClickWrapper';
import * as keyboard from 'util/Keyboard';
import {verticallyScrollItemIntoView} from 'util/Scroll';
import {ElementKeyboardEvent} from 'util/Types';

import {MenuHeight, MenuWidth, OptionContentType, OptionData, OptionValue} from './MenuConstants';
import {getSelectableIndices, hoverToSelectableIndex, isOptionSelectable} from './MenuUtil';
import Section from './Section';
import SectionHeader from './SectionHeader';
import withDropdownCloseControl, {DropdownCloseControlProps} from './withDropdownCloseControl';
import withMenuNavigationLoading, {ConnectedMenuProps} from './withMenuNavigationLoading';

export interface SearchableMenuProps extends DropdownCloseControlProps {
  className?: string;
  dropLeft?: boolean;
  dataTestAttrs?: DataTestAttrs;
  scrollable?: boolean;
  getMatchingOptionDatas?: (query: string, optionDatas: Array<OptionData>) => Array<OptionData>;
  height?: MenuHeight;
  itemContainerClassName?: string;
  onSelect: (value: OptionValue) => void;
  optionDatas: Array<OptionData>;
  renderOptionContent?: (optionData: OptionData, shouldFade?: boolean) => JSX.Element;
  selectedValue?: OptionValue;
  style?: object;
  width?: MenuWidth;
  footer?: JSX.Element;
  isMulti?: boolean;
  // For multi-select dropdowns, allow skipping the default checkmark icon rendered next to the search bar
  skipIconOverride?: boolean;
  spellCheck?: boolean;
  scrollableClassName?: string;
  // When true, renders selected items as chips inside the search input field
  showSelectedAsChips?: boolean;
}

interface State {
  query: string;
  items: Array<OptionData>;
  hasSubmenus: boolean;
}

function isElement(element: Element | Text | null): element is Element {
  return Boolean(element && (element as Element).id != null);
}

export type SearchableMenuArchivableOptionData = OptionData & ArchivableOptionData;
export type SearchableMenuArchivableOptionDatas = Array<SearchableMenuArchivableOptionData>;

interface SelectedItemChipProps {
  name: string;
  onRemove: () => void;
}

function SelectedItemChip({name, onRemove}: SelectedItemChipProps): JSX.Element {
  return (
    <span className={css(chipStyles.chip)}>
      <span className={css(chipStyles.chipText)} title={name}>
        {name}
      </span>
      <BenchlingIcon
        className={css(chipStyles.removeIcon)}
        icon="cross-thick"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          e.preventDefault();
          onRemove();
        }}
      />
    </span>
  );
}

const chipStyles = StyleSheet.create({
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: GRAY_LIGHTER,
    borderRadius: '4px',
    padding: '2px 4px 2px 6px',
    fontSize: '12px',
    lineHeight: '16px',
    maxWidth: '120px',
    marginRight: '4px',
    marginLeft: '4px',
    flexShrink: 0,
  },
  chipText: {
    color: BLUE_LIGHT,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginRight: '5px',
  },
  removeIcon: {
    cursor: 'pointer',
    color: GRAY,
    fontSize: '10px',
    flexShrink: 0,
    ':hover': {
      color: GRAY_DARKER,
    },
  },
});

/*
 * A component that allows filtering menu options via a simple text search.
 * This component allows menu items to have a submenu (only one layer; no nested submenus), with the
 * following search behavior:
 *
 *   - Typing in the search input automatically collapses submenus
 *   - The forward slash `/` is used to denote submenu; e.g. 'item / submenuitem' is a valid search query
 *     for `submenuitem` located in the submenu of `item`
 *   - Trailing spaces around `/` are not necessary; e.g. `item/submenuitem` and `item / submenuitem` are
 *     both valid queries for the same submenuitem
 *   - Standard up/down/enter keyboard navigation when there are not submenus
 *
 * Searching is case-insensitive.
 *
 * Internally, submenus are rendered as HoverFloats so that the menu and submenus can scroll horizontally
 * while the submenus are not constrained in the menu's scrollable container. The downside of this approach
 * is that the submenu contents are only rendered after the parent menu item is hovered over. Consequently,
 * to access any submenu items in enzyme tests, a mock user interaction is needed first. To open all
 * submenus, call SearchableMenuHarness#openDropdownIfNecessary() or testOnlySetOpenAllSubmenus().
 *
 * To allow the submenu item clicks to close the upstream dropdown, a _onCloseDropdown is passed in through
 * the withDropdownCloseControl wrapper. This is necessary because:
 * - If we allow the submenu item click event to propagate from the submenu to the root menu, the dropdown
 *   can be closed automatically. However, this also means each submenu item click will undesirably
 *   trigger the root menu item, for reasons not entirely clear. So we have to stop the click propagation.
 * - Without the click propagation, the upstream dropdown cannot be closed automatically, thus
 *   a manual _onCloseDropdown prop is needed.
 * The _onCloseDropdown prop is read from a context instead of getting passed in directly. This is
 * because we have lots of instances where SearchableMenu is not directly passed to the Dropdown#menu prop,
 * but wrapped under one or multiple layers. To minimize the cognitive load and automatically get the
 * _onCloseDropdown helper:
 * - the Dropdown#render wraps the menu prop with the CloseableDropdownContext.
 * - The SearchableMenu is wrapped with withDropdownCloseControl to get this function from the context.
 *   This extra wrapper is necessary because SearchableMenu is a class component and cannot call a hook.
 * Once the SearchableMenu is migrated to a function component, we can remove this React jujitsu.
 */
export class SearchableMenu extends React.Component<SearchableMenuProps & ConnectedMenuProps, State> {
  static defaultProps: Partial<SearchableMenuProps> = {
    getMatchingOptionDatas,
    height: Menu.HEIGHTS.MEDIUM,
    width: Menu.WIDTHS.LARGE,
    spellCheck: true,
  };

  scrollAreaRef = React.createRef<Menu>();
  private optionRefs: {[ref: string]: HTMLLIElement | null} = {};

  constructor(props: SearchableMenuProps & ConnectedMenuProps) {
    super(props);
    const _getMatchingOptionDatas = props.getMatchingOptionDatas ?? getMatchingOptionDatas;
    this.state = {
      query: '',
      items: _getMatchingOptionDatas('', props.optionDatas),
      hasSubmenus: hasSubmenus(props.optionDatas),
    };
  }

  private getSelectedItems = (): Array<OptionData> => {
    if (!this.props.showSelectedAsChips || !this.props.isMulti) {
      return [];
    }

    if (!this.props.selectedValue) {
      return [];
    }

    // Handle both single and multiple selected values
    const selectedValues = Array.isArray(this.props.selectedValue)
      ? this.props.selectedValue
      : [this.props.selectedValue];
    return this.props.optionDatas.filter((option) =>
      selectedValues.some((value) => isEqual(value, option.value))
    );
  };

  override componentDidMount(): void {
    this.props.onUpdateSelectableIndices(getSelectableIndices(this.props.optionDatas));
  }

  public override UNSAFE_componentWillReceiveProps = (
    nextProps: SearchableMenuProps & ConnectedMenuProps
  ): void => {
    const _getMatchingOptionDatas = nextProps.getMatchingOptionDatas ?? this.props.getMatchingOptionDatas;
    const nextMatchingOptionDatas = _getMatchingOptionDatas!(this.state.query, nextProps.optionDatas);

    // Note: this deep-equality check is dangerous. The `optionDatas` type is unspecified, and may have
    // things like arrow functions or circular references, leading to unexpected re-renders. Buyer beware!
    if (!isEqual(this.state.items, nextMatchingOptionDatas)) {
      nextProps.onUpdateSelectableIndices(getSelectableIndices(nextProps.optionDatas));
      this.setState({
        items: nextMatchingOptionDatas,
      });
    }
  };

  private isKeyboardNavigationEnabled = (): boolean =>
    !this.state.hasSubmenus && this.props.selectableIndices.length > 0;

  private getMenuChildren = (
    menuItems: Array<Array<OptionData>>,
    menuProps: Partial<MenuProps>,
    isRootMenu: boolean
  ): Array<JSX.Element> => {
    const renderOptionContentFn = this.props.renderOptionContent || defaultRenderOptionContent;
    const keyboardNavigationEnabled = this.isKeyboardNavigationEnabled();
    return menuItems.map((section, sectionIndex) => (
      <Section key={sectionIndex.toString()}>
        {section.map((optionData, index) => {
          if (optionData.isHeader) {
            return (
              <SectionHeader className={optionData.className} key={index}>
                {optionData.name}{' '}
              </SectionHeader>
            );
          }
          return (
            <OptionContainer
              className={optionData.className}
              linkClassName={this.props.itemContainerClassName}
              isHovered={
                keyboardNavigationEnabled
                  ? index === hoverToSelectableIndex(this.props.selectableIndices, this.props.hoverIndex)
                  : undefined
              }
              key={index}
              isDisabled={optionData.disabled}
              isSelected={isEqual(this.props.selectedValue, optionData.value)}
              onHover={() => {
                if (!keyboardNavigationEnabled) {
                  return;
                }
                this.props.onHoverOption(index);
              }}
              onMouseOut={() => {
                // Remove the selected hover option once the user's mouse leaves the option. This will get
                // picked up by the onHoverOption callback supplied by withMenuNavigationLoading
                this.props.onHoverOption(-1);
              }}
              // If the click event is propagated, clicking on a submenu item will also trigger the parent
              // menu item's onSelect event, even though the submenu items are rendered in a Float. As a
              // workaround, we stop the propagation here.
              // We stop propagation if this option _has_ a submenu. There is a layer of `Float` between this
              // `OptionContainer` and the child menu. When checking for submenus, `Menu` only looks at
              // immediate children of the `OptionContainer`. `Float` does not have a `_role` prop, so `Menu`
              // thinks it is presentational `OptionContainer` content and does not inject `_submenu`, which
              // would itself disable propagation
              disableEventPropagation={!isRootMenu || optionData.submenu != null}
              onSelect={(value: OptionValue) => {
                if (isOptionSelectable(optionData)) {
                  this.props.onSelect(value);
                  // Because the click event is not propagated to the parent, the upstream dropdown
                  // won't close automatically after the click. So we need to manually close it here.
                  // The _onRequestClose is passed in from the Dropdown component. See the docstring
                  // of SearchableMenu for details.
                  if (!isRootMenu) {
                    this.props._onCloseDropdown?.();
                  }
                }
              }}
              value={optionData.value}
              ref={(node) => {
                if (node != null) {
                  this.optionRefs[getOptionRefForIndex(index)] = node.getRef();
                }
              }}
              dataTestAttrs={optionData.dataTestAttrs}
            >
              {optionData.submenu != null ? (
                <MenuOptionWithFloatingSubmenu
                  menuOptionContainer={this.optionRefs[getOptionRefForIndex(index)]}
                  menuOptionIndex={index}
                  optionData={optionData}
                  renderOptionContentFn={renderOptionContentFn}
                  submenuProps={menuProps}
                  openSubmenuForTesting={this.props.openAllSubmenus}
                >
                  {this.getMenuChildren(
                    (optionData.submenu ?? []).map((os) => os.flatMap((o) => (o ? [o] : []))),
                    menuProps,
                    false
                  )}
                </MenuOptionWithFloatingSubmenu>
              ) : (
                renderOptionContentFn(optionData)
              )}
            </OptionContainer>
          );
        })}
      </Section>
    ));
  };

  private scrollIndexIntoView = (hoverIndex: number): void => {
    const selectableIndex = hoverToSelectableIndex(this.props.selectableIndices, hoverIndex);
    const scrollArea = ReactDOM.findDOMNode(this.scrollAreaRef.current);
    const itemBox = this.optionRefs[getOptionRefForIndex(selectableIndex)];
    if (isElement(scrollArea) && isElement(itemBox)) {
      verticallyScrollItemIntoView(scrollArea, itemBox);
    }
  };

  private changeHovered = (newHoverIndex: number): void => {
    const clampedHoverIndex = this.props.onChangeHoverIndex(newHoverIndex);
    this.scrollIndexIntoView(clampedHoverIndex);
  };

  private handleKeyDown = (e: ElementKeyboardEvent): void => {
    if (!this.isKeyboardNavigationEnabled()) {
      return;
    }

    const {hoverIndex} = this.props;
    if (keyboard.isUpKey(e)) {
      this.changeHovered(hoverIndex - 1);
      e.preventDefault(); // Prevent up from moving text cursor to the beginning of line
    } else if (keyboard.isDownKey(e)) {
      this.changeHovered(hoverIndex + 1);
    } else if (keyboard.isEnterKey(e)) {
      this.props.onSelect(
        this.state.items[hoverToSelectableIndex(this.props.selectableIndices, hoverIndex)].value
      );
      // Because the keydown event is not propagated to the parent, the upstream dropdown won't close
      // automatically after the keydown. So we need to manually close it here. The _onRequestClose is passed
      // in from the Dropdown component. See the docstring of SearchableMenu for details.
      this.props._onCloseDropdown?.();
    } else if (
      keyboard.isBackspaceKey(e) &&
      this.state.query.length === 0 &&
      this.props.showSelectedAsChips
    ) {
      this.handleRemoveSelectedItem(this.getSelectedItems()[this.getSelectedItems().length - 1]);
    }
  };

  private handleQuery = (query: string): void => {
    const items = this.props.getMatchingOptionDatas!(query, this.props.optionDatas);
    this.props.onUpdateSelectableIndices(getSelectableIndices(items));
    this.setState({
      query,
      items,
      hasSubmenus: hasSubmenus(items),
    });
  };

  private handleRemoveSelectedItem = (itemToRemove: OptionData): void => {
    if (!this.props.isMulti) {
      return;
    }

    const selectedItems = this.getSelectedItems();
    const newSelectedItems = selectedItems.filter((item) => !isEqual(item.value, itemToRemove.value));

    // Update selected values in parent by calling onSelect with the updated values
    const newSelectedValues = newSelectedItems.map((item) => item.value);

    // Call onSelect with the updated array of values (excluding the removed item)
    if (newSelectedValues.length > 0) {
      this.props.onSelect(newSelectedValues);
    } else {
      // If no items left, pass empty array
      this.props.onSelect([]);
    }
  };

  private renderSelectedChips = (): JSX.Element | null => {
    if (!this.props.showSelectedAsChips) {
      return null;
    }

    const selectedItems = this.getSelectedItems();
    if (!selectedItems || selectedItems.length === 0) {
      return null;
    }

    return (
      <div className={css(styles.selectedChipsContainer)}>
        {selectedItems.map((item, index) => {
          const name = typeof item.name === 'string' ? item.name : item.pureTextName || item.value;
          return (
            <SelectedItemChip
              key={`selected-${index}-${item.value}`}
              name={name}
              onRemove={() => {
                this.handleRemoveSelectedItem(item);
              }}
            />
          );
        })}
      </div>
    );
  };

  override render(): JSX.Element {
    const selectedItems = this.props.showSelectedAsChips ? this.getSelectedItems() : [];

    const searchInput = (
      <PreventParentClickWrapper>
        <PlainSearchInput
          className="u-width-full"
          clearType={ClearType.X}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            this.handleQuery(e.target.value);
          }}
          onEscape={() => {
            this.handleQuery('');
          }}
          onKeyDown={this.handleKeyDown}
          placeholder={this.props.showSelectedAsChips && selectedItems.length > 0 ? '' : 'Search'}
          shouldAutoSelect={true}
          value={this.state.query}
          spellCheck={this.props.spellCheck}
          renderBeforeInput={this.props.showSelectedAsChips ? this.renderSelectedChips : undefined}
        />
      </PreventParentClickWrapper>
    );
    const sharedParentAndChildMenuProps: Partial<MenuProps> = {
      width: this.props.width,
      height: this.props.height,
      style: this.props.style,
      scrollable: true,
      dropLeft: this.props.dropLeft,
      footer: this.props.footer,
      isMulti: this.props.isMulti,
      scrollableClassName: this.props.scrollableClassName,
    };
    const menuChildren = this.getMenuChildren([this.state.items], sharedParentAndChildMenuProps, true);
    return (
      <Menu
        {...sharedParentAndChildMenuProps}
        dataTestAttrs={this.props.dataTestAttrs}
        value={this.props.selectedValue}
        className={classNames(this.props.className)}
        // The scrollPaddingTop is used to offset the top of the menu to account for the height of the search input;
        // it has no visual impact on what's actually rendered. It matches minHeight for '.menu--medium .menu-option'.
        // Practically, this only impacts Cypress, since we don't autoscroll to an option using anything else.
        style={{scrollPaddingTop: 36, ...this.props.style}}
        ref={this.scrollAreaRef}
        disableKeyboardAccess={true}
      >
        <Section>
          <Option
            isDisabled={true}
            isSelected={true}
            isHovered={false}
            className={css(styles.searchBox)}
            contentType={OptionContentType.INPUT}
            dataTestAttrs={{component: 'SearchableMenu', element: 'option'}}
            skipIconOverride={this.props.skipIconOverride}
          >
            {searchInput}
          </Option>
        </Section>
        {menuChildren}
      </Menu>
    );
  }
}

const SearchableMenuWithDropdownCloseControl = withDropdownCloseControl(SearchableMenu);
const SearchableMenuWithNavigationLoading = withMenuNavigationLoading(SearchableMenuWithDropdownCloseControl);
export default SearchableMenuWithNavigationLoading;

const styles = StyleSheet.create({
  searchBox: {
    position: 'sticky',
    top: 0,
    overflowX: 'auto',
  },
  selectedChipsContainer: {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: 'calc(100% - 50px)',
  },
});
